import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import BottomSheet from '../ui/BottomSheet';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import useExtension from '../../hooks/useExtension';
import type { Extension } from '../../services/extensions';
import { colors, radii, screenPadding, spacing } from '../../theme';

/**
 * "Stay a bit longer", on the running job.
 *
 * ---------------------------------------------------------------------------
 * THE CARD IS THE WHOLE FLOW
 * ---------------------------------------------------------------------------
 * Asking cannot be answered on the spot: the server may hold the Pro at once,
 * or go and free one, which takes people and minutes. Both endings land here,
 * so the card has four faces - offer, pay, waiting, and the answer - and the
 * sheet is only ever the picker for how much time.
 *
 * The countdowns are the point of the middle two. A hold that lapses silently
 * reads as a broken payment button, and "we are arranging it" with no clock
 * reads as nothing happening at all.
 */
type Props = {
  bookingId: string;
  /** False unless the job is running and was sold by the hour. */
  enabled: boolean;
  /** Minutes left before the service's cap on one visit, floored at zero. */
  minutesLeft: number;
  /**
   * When the job is due to finish, as an ISO instant.
   *
   * Two jobs: the sheet says what time the Pro would leave if this were paid
   * for, and the card takes itself off screen once the job is inside the last
   * fifteen minutes - the server refuses an extension then, and an offer that
   * is refused the moment it is tapped is worse than no offer.
   */
  endsAt?: string;
  /** Called once the time is on the booking, so the screen can refetch. */
  onApplied?: () => void;
};

/** "45 min", "1 hr", "1 hr 30 min" - the same wording the server uses. */
const describe = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
};

/**
 * What the sheet offers, trimmed to what is left under the cap.
 *
 * Quarter hours, because that is what the server sells: twenty more minutes
 * should not cost an hour, and holding a Pro for an hour nobody needed is an
 * hour dispatch could have sold to somebody else.
 */
const CHOICES = [15, 30, 45, 60, 90, 120];

/** Matches booking.extensionCutoffMinutes on the server. */
const CUTOFF_MINUTES = 15;

/** `m:ss` while a deadline runs, null once it has passed. */
function useCountdown(until: string | null | undefined): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!until) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [until]);

  if (!until) return null;
  const left = new Date(until).getTime() - now;
  if (left <= 0) return null;
  const seconds = Math.ceil(left / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** The clock time a paid extension carries the job to. */
const endTimeOf = (extension: Extension): string =>
  new Date(extension.toEndAt).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });

function ExtendTimeCard({
  bookingId,
  enabled,
  minutesLeft,
  endsAt,
  onApplied,
}: Props) {
  const { current, busy, error, ask, pay, cancel, dismiss } = useExtension(
    bookingId,
    enabled,
    onApplied,
  );
  const [picking, setPicking] = useState(false);
  const [minutes, setMinutes] = useState(30);

  const choices = useMemo(
    () => CHOICES.filter(choice => choice <= minutesLeft),
    [minutesLeft],
  );

  /* The same cutoff the server keeps, so the card disappears instead of
     offering something that comes back refused. Read once a minute through
     the countdown ticker the card already runs. */
  const tooLate = useMemo(() => {
    if (!endsAt) return false;
    const left = new Date(endsAt).getTime() - Date.now();
    return Number.isFinite(left) && left < CUTOFF_MINUTES * 60_000;
  }, [endsAt]);

  const newEnd = useMemo(() => {
    if (!endsAt) return null;
    const end = new Date(new Date(endsAt).getTime() + minutes * 60_000);
    return Number.isNaN(end.getTime())
      ? null
      : end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }, [endsAt, minutes]);

  const holdLeft = useCountdown(
    current?.status === 'awaiting_payment' ? current.holdExpiresAt : null,
  );
  const opsLeft = useCountdown(
    current?.status === 'awaiting_admin' ? current.adminDeadlineAt : null,
  );

  /* Still drawn while a request is open, however late it is: a customer who
     is mid-payment must not have the card vanish from under them. */
  if (!enabled || choices.length === 0) return null;
  if (tooLate && !current) return null;

  /* Asked first, paid second - deliberately two taps.
     The hourly rate is the booking's own, frozen when it was made, and the
     app cannot derive it from a price that has a discount and a fee in it.
     So the picker offers the time, the server prices it, and the card asks for
     the money with the real figure on the button. */
  const confirm = async () => {
    const asked = await ask(minutes);
    if (asked) setPicking(false);
  };

  const body = () => {
    if (current?.status === 'awaiting_payment') {
      return (
        <>
          <View style={styles.line}>
            <Icon name="clock" size={16} color={colors.primary} />
            <Text variant="labelSm" style={styles.fill}>
              {describe(current.minutes)} more · ₹{Number(current.amount)}
            </Text>
            {holdLeft ? (
              <Text variant="caption" color={colors.inkMuted}>
                {holdLeft} left
              </Text>
            ) : null}
          </View>
          <View style={styles.actions}>
            <Button
              label={holdLeft ? `Pay ₹${Number(current.amount)}` : 'Ask again'}
              size="sm"
              loading={busy}
              onPress={holdLeft ? pay : dismiss}
              style={styles.fill}
            />
            {/* The way out of a wrong choice. Without it the only way to ask
                for a different length is to wait out the hold, which reads as
                the app having locked itself. */}
            {holdLeft ? (
              <Button
                label="Change"
                variant="outline"
                size="sm"
                fullWidth={false}
                disabled={busy}
                onPress={() => {
                  cancel()
                    .then(() => setPicking(true))
                    .catch(() => {});
                }}
              />
            ) : null}
          </View>
        </>
      );
    }

    if (current?.status === 'awaiting_admin') {
      return (
        <>
          <View style={styles.line}>
            <ActivityIndicator size="small" color={colors.primary} />
            <Text variant="labelSm" style={styles.fill}>
              Finding you a professional
            </Text>
            {opsLeft ? (
              <Text variant="caption" color={colors.inkMuted}>
                {opsLeft}
              </Text>
            ) : null}
          </View>
          {/* Said plainly, because the spinner alone reads as the app
              thinking. A person at Homingo is doing this, it takes minutes,
              and the answer arrives on its own - a customer told that waits;
              one who is not, taps. */}
          <Text variant="caption" color={colors.inkMuted}>
            Your request is with our team. They are arranging someone, and we
            will let you know either way.
          </Text>
          <Button
            label="Cancel"
            variant="outline"
            size="sm"
            disabled={busy}
            onPress={() => {
              cancel().catch(() => {});
            }}
          />
        </>
      );
    }

    if (current?.status === 'paid') {
      return (
        <View style={styles.line}>
          <Icon
            name="check"
            size={16}
            color={colors.primary}
            strokeWidth={2.4}
          />
          <Text variant="labelSm" style={styles.fill}>
            Extended to {endTimeOf(current)}
          </Text>
        </View>
      );
    }

    if (current) {
      /* Declined, lapsed, or paid-but-not-applied. The wording is the same
         for all three on purpose - the customer's next move is identical,
         and "your Pro was taken" is not a thing to read while they work. */
      return (
        <>
          <View style={styles.line}>
            <Icon name="clock" size={16} color={colors.inkMuted} />
            <Text variant="labelSm" color={colors.inkMuted} style={styles.fill}>
              No one free to stay on
            </Text>
          </View>
          <Button
            label="Try again"
            size="sm"
            variant="outline"
            onPress={dismiss}
          />
        </>
      );
    }

    return (
      <>
        <View style={styles.line}>
          <Icon name="clock" size={16} color={colors.primary} />
          <Text variant="labelSm" style={styles.fill}>
            Need more time?
          </Text>
        </View>
        <Button
          label="Extend"
          size="sm"
          loading={busy}
          onPress={() => setPicking(true)}
        />
      </>
    );
  };

  return (
    <View style={styles.card}>
      {body()}
      {error ? (
        <Text variant="caption" color={colors.danger}>
          {error}
        </Text>
      ) : null}

      <BottomSheet
        visible={picking}
        onClose={() => setPicking(false)}
        footer={
          <Button
            label={busy ? 'Checking' : 'Continue'}
            loading={busy}
            onPress={() => {
              confirm().catch(() => {});
            }}
          />
        }
      >
        {/* The sheet pads nothing for its children - every caller of it
            brings its own gutter, and without one the heading sits on the
            screen edge. */}
        <View style={styles.sheet}>
          <Text variant="subtitle">Add time</Text>
          <Text variant="caption" color={colors.inkMuted}>
            Your professional stays on, if they are free.
          </Text>

          <View style={styles.choices}>
            {choices.map(choice => (
              <Button
                key={choice}
                label={describe(choice)}
                variant={choice === minutes ? 'primary' : 'outline'}
                size="sm"
                fullWidth={false}
                onPress={() => setMinutes(choice)}
                style={styles.choice}
              />
            ))}
          </View>

          {/* What the choice actually buys, in the one unit that matters to
              somebody with a professional in their kitchen: the time they
              leave. The price is the server's to state, and it does, on the
              card behind this sheet. */}
          {newEnd ? (
            <View style={styles.line}>
              <Icon name="clock" size={14} color={colors.inkMuted} />
              <Text variant="caption" color={colors.inkMuted}>
                Would finish around {newEnd}
              </Text>
            </View>
          ) : null}
        </View>
      </BottomSheet>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.sm,
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surfaceMuted,
  },
  line: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  fill: {
    flex: 1,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  sheet: {
    gap: spacing.xs,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.lg,
  },
  choices: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  /* Three to a row on a phone, so the six choices read as a grid rather than
     a ragged paragraph of buttons. */
  choice: {
    flexGrow: 1,
    flexBasis: '30%',
  },
});

export default ExtendTimeCard;
